// @ts-nocheck
/**
 * DeckBuilderCard - デッキ構築画面用カードコンポーネント
 *
 * CardV2 + RarityEffectLayer をラップし、デッキ構築固有のUIを追加
 * - ヘッダー: コスト、レアリティバッジ
 * - フッター: タイプアイコン、使用枚数（n/3）
 * - 情報セクション: カード名、ステータス（モンスターのみ）
 */

import React, { memo } from 'react';
import CardV2 from './CardV2';
import RarityEffectLayer from './RarityEffectLayer';
import { CARD_DISPLAY_SIZES } from './cardConstants';
import { ATTRIBUTE_COLORS } from '../../utils/constants';
import { RARITY_COLORS } from '../../collection/data/constants';

// ========================================
// 型定義
// ========================================

export interface DeckBuilderCardProps {
  /** カードデータ */
  card: {
    id: string;
    name: string;
    attribute: string;
    cost: number;
    type: string;
    attack?: number;
    hp?: number;
    category?: string;
    effect?: string;
  };

  /** レアリティ */
  rarity: string;

  /** 使用枚数 */
  usedCount: number;

  /** 上限枚数（デフォルト: 3） */
  maxCount?: number;

  /** エフェクトレベル */
  effectLevel?: 'full' | 'minimal' | 'off';

  /** クリックハンドラ */
  onClick?: () => void;

  /** ホバーハンドラ */
  onMouseEnter?: () => void;

  /** モバイル表示 */
  isMobile?: boolean;
}

// ========================================
// 定数
// ========================================

const TYPE_ICONS = {
  monster: '⚔️',
  magic: '✨',
  field: '🏔️',
  phasecard: '🔮',
};

// ========================================
// スタイル定義
// ========================================

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
  },
  cardWrapper: {
    position: 'relative' as const,
    overflow: 'hidden',
    borderRadius: '8px',
  },
  header: {
    padding: '3px 5px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(0,0,0,0.6)',
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  costBadge: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold' as const,
    color: '#fff',
  },
  rarityBadge: {
    padding: '1px 4px',
    borderRadius: '3px',
    fontSize: '9px',
    fontWeight: 'bold' as const,
    color: '#fff',
  },
  footer: {
    padding: '3px 5px',
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  typeIcon: {
    fontSize: '11px',
  },
  usageCountBadge: {
    fontSize: '10px',
    fontWeight: 'bold' as const,
    padding: '1px 4px',
    borderRadius: '3px',
  },
  infoSection: {
    padding: '5px 3px',
    background: 'rgba(20, 20, 40, 0.95)',
    borderRadius: '0 0 4px 4px',
    marginTop: '2px',
  },
  cardName: {
    fontSize: '10px',
    fontWeight: 'bold' as const,
    textAlign: 'center' as const,
    color: '#fff',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    lineHeight: '1.3',
  },
  cardStats: {
    fontSize: '9px',
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center' as const,
    marginTop: '2px',
    lineHeight: '1.2',
  },
};

// ========================================
// メインコンポーネント
// ========================================

const DeckBuilderCard: React.FC<DeckBuilderCardProps> = memo(({
  card,
  rarity,
  usedCount,
  maxCount = 3,
  effectLevel = 'full',
  onClick,
  onMouseEnter,
  isMobile = false,
}) => {
  const rarityColor = RARITY_COLORS[rarity] || '#808080';
  const isMonster = card.type === 'monster';

  // 使用状況に基づく色
  const isMaxed = usedCount >= maxCount;
  const usageColor = isMaxed
    ? '#ef4444'  // 上限: 赤
    : usedCount > 0
      ? '#4caf50'  // 使用中: 緑
      : 'rgba(255,255,255,0.5)';  // 未使用: グレー

  // サイズ計算
  const sizePreset = isMobile
    ? CARD_DISPLAY_SIZES.deckBuilderMobile
    : CARD_DISPLAY_SIZES.deckBuilder;
  const cardWidth = sizePreset.width;

  // カード画像部分のアスペクト比を維持（3:4）
  const imageHeight = Math.floor(cardWidth * (4 / 3));

  return (
    <div
      style={{
        ...styles.container,
        width: cardWidth,
        opacity: isMaxed ? 0.7 : 1,
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      {/* カード画像部分（RarityEffectLayer + CardV2） */}
      <RarityEffectLayer
        rarity={rarity}
        effectLevel={effectLevel}
        width={cardWidth}
        height={imageHeight}
        enableMouseEffect={effectLevel === 'full'}
        hoverTransform="translateY(-3px) scale(1.02)"
      >
        {/* ヘッダー: コスト & レアリティ */}
        <div style={styles.header}>
          <div style={styles.costBadge}>{card.cost}</div>
          <div
            style={{
              ...styles.rarityBadge,
              background: rarityColor,
            }}
          >
            {rarity}
          </div>
        </div>

        {/* CardV2（画像のみ） */}
        <CardV2
          card={card}
          displayMode="deckBuilder"
          displayWidth={cardWidth}
          isMobile={isMobile}
        />

        {/* フッター: タイプ & 使用枚数 */}
        <div style={styles.footer}>
          <span style={styles.typeIcon}>
            {TYPE_ICONS[card.type] || '📄'}
          </span>
          <span
            style={{
              ...styles.usageCountBadge,
              background: usageColor,
              color: '#fff',
            }}
          >
            {usedCount}/{maxCount}
          </span>
        </div>
      </RarityEffectLayer>

      {/* 情報セクション: 名前 & ステータス */}
      <div style={styles.infoSection}>
        <div style={styles.cardName}>{card.name}</div>
        {isMonster && (
          <div style={styles.cardStats}>
            ATK {card.attack} / HP {card.hp}
          </div>
        )}
      </div>
    </div>
  );
});

DeckBuilderCard.displayName = 'DeckBuilderCard';

export default DeckBuilderCard;
